import { MarketplaceIds, isEUMarketplace } from 'src/utils/marketplace';
import { DealType } from '@amzn/handshake-painter-typescript-client/v1';
export var CurrencyPosition;
(function (CurrencyPosition) {
    CurrencyPosition[CurrencyPosition["LEFT"] = 0] = "LEFT";
    CurrencyPosition[CurrencyPosition["RIGHT"] = 1] = "RIGHT";
})(CurrencyPosition || (CurrencyPosition = {}));
// TODO: Move these constants to the model package as enums
// Price prominence constants
export const PRICE_PROMINENCE = {
    SAME_AS_PRIMARY: 'SAME_AS_PRIMARY',
};
// Price types
export const PRICE_TYPES = {
    OMNIBUS_PRICE: 'OMNIBUS_PRICE',
    OMNIBUS_PRICE_STRIKETHROUGH: 'OMNIBUS_PRICE_STRIKETHROUGH',
};
export const formatPrice = (amount = 0, currencyCode = 'USD', locale) => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        useGrouping: true,
    }).format(amount);
};
export const parsePriceComponents = (amount = 0, currency = 'USD', locale) => {
    var _a;
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        useGrouping: true,
    });
    const parts = formatter.formatToParts(amount);
    const currencySymbol = parts.find((part) => part.type === 'currency').value;
    const basePart = parts
        .filter((part) => part.type === 'integer' || part.type === 'group')
        .map((part) => part.value)
        .join('');
    const fractionalPart = ((_a = parts.find((part) => part.type === 'fraction')) === null || _a === void 0 ? void 0 : _a.value) || '';
    const currencyIndex = parts.findIndex((part) => part.type === 'currency');
    const integerIndex = parts.findIndex((part) => part.type === 'integer');
    const currencySymbolPosition = currencyIndex < integerIndex ? CurrencyPosition.LEFT : CurrencyPosition.RIGHT;
    return {
        currencySymbol,
        basePart,
        fractionalPart,
        currencySymbolPosition,
    };
};
/**
 * Utility functions for price formatting, parsing, and display logic.
 * Handles marketplace-specific pricing rules and discount calculations.
 */
/**
 * Determines whether to show deal badge/styling based on marketplace rules.
 *
 * US/CA marketplaces: Show deal only when discount percentage exists
 * Other marketplaces: Show deal when any deal type is present
 *
 * @param marketplaceId - The marketplace identifier (e.g., 'US', 'FR', 'CA')
 * @param dealDetails - Optional deal information including type and discount
 * @returns true if deal should be displayed
 */
export const shouldShowDeal = (marketplaceId, dealDetails) => {
    // NONE means the product has a discount but no promotional deal: show the
    // discount (handled by shouldShowDiscountPercentage) but never a deal badge.
    if ((dealDetails === null || dealDetails === void 0 ? void 0 : dealDetails.type) === DealType.None) {
        return false;
    }
    // EU-specific behavior: show deal when any deal type is present
    if (isEUMarketplace(marketplaceId)) {
        return !!(dealDetails === null || dealDetails === void 0 ? void 0 : dealDetails.type);
    }
    // Default behavior (US/CA)
    return !!(dealDetails === null || dealDetails === void 0 ? void 0 : dealDetails.discountPercentage);
};
/**
 * Calculates discount percentage visibility and styling variant based on price configuration.
 *
 * Handles three scenarios:
 * 1. Single reference price (originalPrice only)
 * 2. Dual reference prices with different prominence
 * 3. Dual reference prices with SAME_AS_PRIMARY prominence
 *
 * Special case: FR marketplace uses light variant (gray) for non-Omnibus prices
 * to comply with local pricing display regulations.
 *
 * @param showOriginalPrice - Whether original price exists
 * @param showSecondaryReferencePrice - Whether secondary reference price exists
 * @param isSameAsPrimary - Whether secondary price has SAME_AS_PRIMARY prominence
 * @param marketplaceId - The marketplace identifier
 * @param dealDetails - Optional deal information
 * @param originalPrice - Optional original price data
 * @returns Object with showDiscountPercentage and shouldUseLightVariant booleans
 */
export const shouldShowDiscountPercentage = (showOriginalPrice, showSecondaryReferencePrice, isSameAsPrimary, marketplaceId, dealDetails, originalPrice) => {
    // Check if original price is Omnibus type (EU regulatory requirement)
    const isOriginalPriceOmnibusPrice = (originalPrice === null || originalPrice === void 0 ? void 0 : originalPrice.type) === PRICE_TYPES.OMNIBUS_PRICE ||
        (originalPrice === null || originalPrice === void 0 ? void 0 : originalPrice.type) === PRICE_TYPES.OMNIBUS_PRICE_STRIKETHROUGH;
    // Show discount percentage for single price OR dual price when not SAME_AS_PRIMARY
    const showOriginalPriceDiscount = !!(dealDetails === null || dealDetails === void 0 ? void 0 : dealDetails.discountPercentage);
    const showDiscountPercentage = (showOriginalPrice && !showSecondaryReferencePrice && showOriginalPriceDiscount) ||
        (showOriginalPrice && showSecondaryReferencePrice && !isSameAsPrimary && showOriginalPriceDiscount);
    // Use light variant for FR marketplace when price is not Omnibus
    // This distinguishes between Omnibus prices and other reference prices in FR marketplace
    const shouldUseLightVariant = showOriginalPrice &&
        !showSecondaryReferencePrice &&
        !isOriginalPriceOmnibusPrice &&
        marketplaceId === MarketplaceIds.FR;
    return { showDiscountPercentage, shouldUseLightVariant };
};
